import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuth } from './hooks/useAuth';
import Navbar from './components/layout/Navbar';
import Sidebar from './components/layout/Sidebar';
import LoadingSpinner from './components/common/LoadingSpinner';
import ScrollToTopButton from './components/common/ScrollToTopButton';
import ScrollRestoration from './components/common/ScrollRestoration';

import Landing from './pages/Landing';
import ContactUs from './pages/ContactUs';
import AboutUs from './pages/AboutUs';
import Blog from './pages/Blog';
import Faqs from './pages/Faqs';
import Services from './pages/Services';
import Careers from './pages/Careers';
import Press from './pages/Press';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import CookiePolicy from './pages/CookiePolicy';
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ForgotPassword from './pages/auth/ForgotPassword';
import Dashboard from './pages/user/Dashboard';
import LoanApplication from './pages/user/LoanApplication';
import CreditCheck from './pages/user/CreditCheck';
import LoanStatus from './pages/user/LoanStatus';
import LoanDetail from './pages/user/LoanDetail';
import Documents from './pages/user/Documents';
import Repayments from './pages/user/Repayments';
import Profile from './pages/user/Profile';
import Notifications from './pages/user/Notifications';
import Support from './pages/user/Support';
import Settings from './pages/user/Settings';
import ContributionPlans from './pages/user/ContributionPlans';
import NewContributionPlan from './pages/user/NewContributionPlan';
import ContributionPlanDetail from './pages/user/ContributionPlanDetail';
import Groups from './pages/user/Groups';
import NewGroup from './pages/user/NewGroup';
import GroupDetail from './pages/user/GroupDetail';
import CreditReport from './pages/user/CreditReport';
import PaymentComplete from './pages/user/PaymentComplete';

import AdminDashboard from './pages/admin/AdminDashboard';
import CheckerDashboard from './pages/admin/CheckerDashboard';
import MakerDashboard from './pages/admin/MakerDashboard';
import ApplicationsList from './pages/admin/ApplicationsList';
import LoanApplicationReview from './pages/admin/LoanApplicationReview';
import UsersList from './pages/admin/UsersList';
import KYCReview from './pages/admin/KYCReview';
import Reports from './pages/admin/Reports';
import Analytics from './pages/admin/Analytics';
import ApprovalQueue from './pages/admin/ApprovalQueue';
import AuditLogs from './pages/admin/AuditLogs';
import Disbursements from './pages/admin/Disbursements';
import DisbursementOfficerDashboard from './pages/admin/DisbursementOfficerDashboard';
import KYCOfficerDashboard from './pages/admin/KYCOfficerDashboard';
import FraudAlerts from './pages/admin/FraudAlerts';
import RoleManagement from './pages/admin/RoleManagement';
import LoanProducts from './pages/admin/LoanProducts';
import ContributionPlansAdmin from './pages/admin/ContributionPlansAdmin';
import GroupsAdmin from './pages/admin/GroupsAdmin';
import { CoopSocietiesList, CoopSocietyNew, CoopSocietyDetail } from './pages/admin/CoopSocieties';

function AppLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', background: 'transparent' }}>
      <Navbar onMenuClick={() => setSidebarOpen(o => !o)} />
      <div style={{ display: 'flex', flex: 1 }}>
        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <main className="app-main" style={{ flex: 1, overflow: 'auto', maxWidth: '100%' }}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function RequireAuth({ children, adminOnly = false, auditorsAllowed = false, disbursementAllowed = false, kycOfficerAllowed = false }) {
  const { isAuthenticated, isStaff, isAuditor, isDisbursementOfficer, isKycOfficer, loading } = useAuth();
  const location = useLocation();
  if (loading) return <LoadingSpinner />;
  if (!isAuthenticated) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  if (adminOnly && !isStaff && !isAuditor && !isDisbursementOfficer && !isKycOfficer) return <Navigate to="/dashboard" replace />;
  if (adminOnly && isAuditor && !auditorsAllowed) return <Navigate to="/admin/audit-logs" replace />;
  if (adminOnly && isDisbursementOfficer && !disbursementAllowed) return <Navigate to="/admin/disbursement" replace />;
  if (adminOnly && isKycOfficer && !kycOfficerAllowed) return <Navigate to="/admin/kyc-officer" replace />;
  return children;
}

function AppRoutes() {
  const { loadProfile, isAuthenticated } = useAuth();

  useEffect(() => {
    if (isAuthenticated) loadProfile();
  }, [isAuthenticated, loadProfile]);

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/contact" element={<ContactUs />} />
      <Route path="/about" element={<AboutUs />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/faqs" element={<Faqs />} />
      <Route path="/services" element={<Services />} />
      <Route path="/careers" element={<Careers />} />
      <Route path="/press" element={<Press />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/cookies" element={<CookiePolicy />} />
      <Route element={<RequireAuth><AppLayout /></RequireAuth>}>
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="loans" element={<LoanStatus />} />
        <Route path="loans/credit-check" element={<CreditCheck />} />
        <Route path="loans/apply" element={<LoanApplication />} />
        <Route path="loans/:id" element={<LoanDetail />} />
        <Route path="loans/:id/repayments" element={<Repayments />} />
        <Route path="documents" element={<Documents />} />
        <Route path="contributions" element={<ContributionPlans />} />
        <Route path="contributions/new" element={<NewContributionPlan />} />
        <Route path="contributions/:id" element={<ContributionPlanDetail />} />
        <Route path="groups" element={<Groups />} />
        <Route path="groups/new" element={<NewGroup />} />
        <Route path="groups/:id" element={<GroupDetail />} />
        <Route path="credit-report" element={<CreditReport />} />
        <Route path="payment-complete" element={<PaymentComplete />} />
        <Route path="profile" element={<Profile />} />
        <Route path="notifications" element={<Notifications />} />
        <Route path="support" element={<Support />} />
        <Route path="settings" element={<Settings />} />
      </Route>
      <Route element={<RequireAuth adminOnly><AppLayout /></RequireAuth>}>
        <Route path="admin/dashboard" element={<AdminDashboard />} />
        <Route path="admin/checker" element={<CheckerDashboard />} />
        <Route path="admin/maker"   element={<MakerDashboard />} />
        <Route path="admin/applications" element={<ApplicationsList />} />
        <Route path="admin/applications/:id" element={<LoanApplicationReview />} />
        <Route path="admin/users" element={<UsersList />} />
        <Route path="admin/kyc" element={<KYCReview />} />
        <Route path="admin/analytics" element={<Analytics />} />
        <Route path="admin/reports" element={<Reports />} />
        <Route path="admin/approvals" element={<ApprovalQueue />} />
        <Route path="admin/disbursements" element={<Disbursements />} />
        <Route path="admin/fraud" element={<FraudAlerts />} />
        <Route path="admin/roles" element={<RoleManagement />} />
        <Route path="admin/loan-products" element={<LoanProducts />} />
        <Route path="admin/contribution-plans" element={<ContributionPlansAdmin />} />
        <Route path="admin/groups" element={<GroupsAdmin />} />
        <Route path="admin/coop-societies" element={<CoopSocietiesList />} />
        <Route path="admin/coop-societies/new" element={<CoopSocietyNew />} />
        <Route path="admin/coop-societies/:id" element={<CoopSocietyDetail />} />
      </Route>
      {/* Audit route — accessible to both staff and auditors */}
      <Route element={<RequireAuth adminOnly auditorsAllowed><AppLayout /></RequireAuth>}>
        <Route path="admin/audit-logs" element={<AuditLogs />} />
      </Route>
      {/* Disbursement officer route */}
      <Route element={<RequireAuth adminOnly disbursementAllowed><AppLayout /></RequireAuth>}>
        <Route path="admin/disbursement" element={<DisbursementOfficerDashboard />} />
      </Route>
      {/* KYC officer route */}
      <Route element={<RequireAuth adminOnly kycOfficerAllowed><AppLayout /></RequireAuth>}>
        <Route path="admin/kyc-officer" element={<KYCOfficerDashboard />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ScrollRestoration />
      <AppRoutes />
      <ScrollToTopButton />
    </BrowserRouter>
  );
}
